"use client"

import { useEffect, useRef, useState } from "react"
import { ChatInput } from "./ChatInput"
import { ChatMessages } from "./ChatMessages"
import { Message } from "./types"

import { useGlobal } from "@/app/context/GlobalContext"
import { TayyibChatClient } from "@/lib/tayyib-client"
import { submitFeedback } from "@/services/feedbackService"
import { useAuth } from "react-oidc-context"
import { useDefaultTool } from "@/hooks/useToolRenderer"
import { ToolCallDisplay } from "./ToolCallDisplay"
import { UtensilsCrossed, ShoppingBag, ChefHat, ScanLine, ShieldCheck, Store, BookOpen } from "lucide-react"

type ChatHistoryItem = { id: string; title: string; timestamp: number }

export default function ChatInterface() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [client, setClient] = useState<TayyibChatClient | null>(null)
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID())
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([])

  const { isLoading, setIsLoading } = useGlobal()
  const auth = useAuth()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scanFileRef = useRef<HTMLInputElement>(null)

  useDefaultTool(({ name, args, status, result }) => (
    <ToolCallDisplay name={name} args={args} status={status} result={result} />
  ))

  // Load config
  useEffect(() => {
    async function loadConfig() {
      try {
        const response = await fetch("/aws-exports.json")
        if (!response.ok) throw new Error("Failed to load configuration")
        const config = await response.json()
        const apiUrl = config.chatApiUrl
        if (!apiUrl) throw new Error("Chat API URL not found in configuration")
        setClient(new TayyibChatClient(apiUrl))
      } catch (err) {
        setError(`Configuration error: ${err instanceof Error ? err.message : "Unknown error"}`)
      }
    }
    loadConfig()
    loadChatHistory()
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Send pending message after sign-in redirect
  useEffect(() => {
    if (auth.isAuthenticated && client) {
      const pending = sessionStorage.getItem("tayyib_pending_message")
      if (pending) {
        sessionStorage.removeItem("tayyib_pending_message")
        sendMessage(pending)
      }
    }
  }, [auth.isAuthenticated, client])

  const loadChatHistory = () => {
    const stored = localStorage.getItem("tayyib_chat_history")
    if (stored) setChatHistory(JSON.parse(stored))
  }

  const saveChatToHistory = (userMessage: string) => {
    const newChat: ChatHistoryItem = {
      id: crypto.randomUUID(),
      title: userMessage.slice(0, 40) + (userMessage.length > 40 ? "..." : ""),
      timestamp: Date.now(),
    }
    const updated = [newChat, ...chatHistory.slice(0, 19)]
    setChatHistory(updated)
    localStorage.setItem("tayyib_chat_history", JSON.stringify(updated))
  }

  const deleteChatFromHistory = (id: string) => {
    const updated = chatHistory.filter(c => c.id !== id)
    setChatHistory(updated)
    localStorage.setItem("tayyib_chat_history", JSON.stringify(updated))
  }

  const handleImageSelect = (base64: string) => {
    // Store the image and send a scan request
    // The agent will call scan_food_label_image with this base64
    sendMessage(`Scan this food label for halal compliance. The image base64 data is: ${base64}`)
  }

  const sendMessage = async (userMessage: string) => {
    if (!userMessage.trim() || !client) return

    // Require auth to send messages
    if (!auth.isAuthenticated) {
      sessionStorage.setItem("tayyib_pending_message", userMessage)
      auth.signinRedirect()
      return
    }

    setError(null)

    const newUserMessage: Message = { role: "user", content: userMessage, timestamp: new Date().toISOString() }
    setMessages(prev => [...prev, newUserMessage])
    setInput("")
    setIsLoading(true)
    saveChatToHistory(userMessage)

    const assistantResponse: Message = { role: "assistant", content: "", timestamp: new Date().toISOString() }
    setMessages(prev => [...prev, assistantResponse])

    try {
      const idToken = auth.user?.id_token
      if (!idToken) throw new Error("Authentication required. Please log in again.")

      const result = await client.sendMessage(userMessage, sessionId, idToken)

      setMessages(prev => {
        const updated = [...prev]
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          content: result.response || "Sorry, I had trouble understanding that.",
        }
        return updated
      })
    } catch (err) {
      setError(`Failed to get response: ${err instanceof Error ? err.message : "Unknown error"}`)
      setMessages(prev => {
        const updated = [...prev]
        updated[updated.length - 1] = { ...updated[updated.length - 1], content: "I apologize, but I encountered an error processing your request. Please try again." }
        return updated
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); sendMessage(input) }

  const handleFeedbackSubmit = async (messageContent: string, feedbackType: "positive" | "negative", comment: string) => {
    try {
      const idToken = auth.user?.id_token
      if (!idToken) throw new Error("Authentication required.")
      await submitFeedback({ sessionId, message: messageContent, feedbackType, comment: comment || undefined }, idToken)
    } catch (err) {
      setError(`Failed to submit feedback: ${err instanceof Error ? err.message : "Unknown error"}`)
    }
  }

  const startNewChat = () => {
    setMessages([])
    setInput("")
    setError(null)
    setSessionId(crypto.randomUUID())
  }

  const isInitialState = messages.length === 0
  const userName = auth.user?.profile?.email?.split("@")[0] || "User"

  return (
    <div className="flex h-screen w-full bg-white">
      {/* Left Sidebar */}
      <div className={`${sidebarOpen ? "w-72" : "w-16"} bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 flex-shrink-0`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img src="/tayyib-06.png" alt="Tayyib" className="h-8" />
                <p className="text-xs text-gray-500">Welcome, {userName}</p>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <img src="/tayyib-06.png" alt="Tayyib" className="h-9" />
              <button onClick={() => setSidebarOpen(true)} className="w-5 h-5 bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center rounded transition-colors">
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* New Chat Button */}
        <div className="p-4">
          <button onClick={startNewChat} className={`w-full flex items-center space-x-3 px-3 py-3 rounded-xl transition-all hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 group ${sidebarOpen ? "" : "justify-center"}`} title="New Chat">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            {sidebarOpen && <span className="text-gray-700 font-medium">New Chat</span>}
          </button>
        </div>

        {/* Chat History */}
        {sidebarOpen && (
          <div className="flex-1 px-4 py-2 overflow-y-auto">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-4">Recent Chats</div>
            {chatHistory.length > 0 ? (
              <div className="space-y-1">
                {chatHistory.map(chat => (
                  <div key={chat.id} className="flex items-center group hover:bg-white hover:shadow-sm rounded-lg">
                    <button onClick={() => { setInput(chat.title.replace("...", "")); }} className="flex-1 text-left px-3 py-2 text-sm text-gray-700 truncate">
                      {chat.title}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteChatFromHistory(chat.id); }} className="opacity-0 group-hover:opacity-100 p-1 mr-2 text-gray-400 hover:text-red-500 transition-all" title="Delete chat">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">No recent chats yet</div>
            )}
          </div>
        )}

        {/* Bottom Section */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          {auth.isAuthenticated ? (
            <button onClick={() => { auth.removeUser(); window.location.href = '/'; }} className={`w-full flex items-center space-x-3 px-3 py-3 rounded-xl transition-all hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 group ${sidebarOpen ? "" : "justify-center"}`} title="Sign Out">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              {sidebarOpen && <span className="text-gray-700 font-medium">Sign Out</span>}
            </button>
          ) : (
            <button onClick={() => auth.signinRedirect()} className={`w-full flex items-center space-x-3 px-3 py-3 rounded-xl transition-all hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 group ${sidebarOpen ? "" : "justify-center"}`} title="Sign In">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              {sidebarOpen && <span className="text-gray-700 font-medium">Sign In</span>}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-2">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {isInitialState ? (
          <div className="grow flex flex-col items-center justify-center px-4 bg-gradient-to-br from-emerald-50 to-white">
            <div className="text-center max-w-2xl mx-auto w-full">
              <div className="mb-6">
                <img src="/logo.png" alt="Tayyib" className="w-24 h-24 mx-auto mb-3" />
                <p className="text-base text-gray-500">Your halal food companion in Japan</p>
              </div>

              <div className="mb-6">
                <ChatInput input={input} setInput={setInput} handleSubmit={handleSubmit} isLoading={isLoading} onImageSelect={handleImageSelect} />
              </div>

              {/* Journey Cards */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <button onClick={() => sendMessage("Where can I eat halal food nearby?")} className="p-4 bg-white hover:bg-emerald-50 rounded-2xl transition-all shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-300 group text-center">
                  <div className="w-11 h-11 mx-auto mb-2 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:bg-emerald-100 transition-colors"><UtensilsCrossed className="w-5 h-5 text-emerald-600" /></div>
                  <div className="text-sm font-semibold text-gray-800">Eat Out</div>
                  <div className="text-xs text-gray-400 mt-1">Halal restaurants</div>
                </button>
                <button onClick={() => sendMessage("Find halal grocery stores near me")} className="p-4 bg-white hover:bg-emerald-50 rounded-2xl transition-all shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-300 group text-center">
                  <div className="w-11 h-11 mx-auto mb-2 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors"><ShoppingBag className="w-5 h-5 text-blue-600" /></div>
                  <div className="text-sm font-semibold text-gray-800">Buy Groceries</div>
                  <div className="text-xs text-gray-400 mt-1">Halal stores</div>
                </button>
                <button onClick={() => sendMessage("Help me cook a halal meal tonight")} className="p-4 bg-white hover:bg-emerald-50 rounded-2xl transition-all shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-300 group text-center">
                  <div className="w-11 h-11 mx-auto mb-2 bg-orange-50 rounded-xl flex items-center justify-center group-hover:bg-orange-100 transition-colors"><ChefHat className="w-5 h-5 text-orange-600" /></div>
                  <div className="text-sm font-semibold text-gray-800">Cook at Home</div>
                  <div className="text-xs text-gray-400 mt-1">Halal recipes</div>
                </button>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => scanFileRef.current?.click()} className="p-3 bg-white hover:bg-gray-50 rounded-xl text-left transition-all border border-gray-100 hover:border-emerald-200 group">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center group-hover:bg-purple-100 transition-colors flex-shrink-0"><ScanLine className="w-4 h-4 text-purple-600" /></div>
                    <span className="text-gray-700 text-sm">Scan a food label</span>
                  </div>
                </button>
                <button onClick={() => sendMessage("Is soy sauce halal in Japan?")} className="p-3 bg-white hover:bg-gray-50 rounded-xl text-left transition-all border border-gray-100 hover:border-emerald-200 group">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center group-hover:bg-emerald-100 transition-colors flex-shrink-0"><ShieldCheck className="w-4 h-4 text-emerald-600" /></div>
                    <span className="text-gray-700 text-sm">Check an ingredient</span>
                  </div>
                </button>
                <button onClick={() => sendMessage("What halal snacks can I find at a convenience store?")} className="p-3 bg-white hover:bg-gray-50 rounded-xl text-left transition-all border border-gray-100 hover:border-emerald-200 group">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition-colors flex-shrink-0"><Store className="w-4 h-4 text-amber-600" /></div>
                    <span className="text-gray-700 text-sm">Konbini halal snacks</span>
                  </div>
                </button>
                <button onClick={() => sendMessage("I want to make chicken biryani for 6 people")} className="p-3 bg-white hover:bg-gray-50 rounded-xl text-left transition-all border border-gray-100 hover:border-emerald-200 group">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center group-hover:bg-rose-100 transition-colors flex-shrink-0"><BookOpen className="w-4 h-4 text-rose-600" /></div>
                    <span className="text-gray-700 text-sm">Chicken biryani recipe</span>
                  </div>
                </button>
              </div>
              {/* Hidden file input for label scanning */}
              <input ref={scanFileRef} type="file" accept="image/*" capture="environment" onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; const reader = new FileReader(); reader.onload = () => { const b64 = (reader.result as string).split(",")[1]; if (b64) handleImageSelect(b64); }; reader.readAsDataURL(file); e.target.value = ""; }} className="hidden" />
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="flex-none border-b border-gray-100 bg-white">
              <div className="max-w-4xl mx-auto w-full flex items-center justify-between px-4 py-3">
                <div className="flex items-center space-x-3">
                  <img src="/tayyib-06.png" alt="Tayyib" className="h-7" />
                  <span className="text-sm text-gray-400">Halal Food Companion</span>
                </div>
                <button onClick={startNewChat} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors">
                  + New Chat
                </button>
              </div>
            </div>
            <div className="grow overflow-hidden">
              <div className="max-w-4xl mx-auto w-full h-full">
                <ChatMessages messages={messages} messagesEndRef={messagesEndRef} sessionId={sessionId} isLoading={isLoading} onFeedbackSubmit={handleFeedbackSubmit} onQuickAction={(action) => sendMessage(action)} />
              </div>
            </div>
            <div className="flex-none">
              <div className="max-w-4xl mx-auto w-full">
                <ChatInput input={input} setInput={setInput} handleSubmit={handleSubmit} isLoading={isLoading} onImageSelect={handleImageSelect} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
